import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchSchedule } from '@apis/schedule';
import { scheduleKey } from '@constants/queryKeys';
import { retryUnlessProviderError } from '@utils/queryRetry';

/**
 * Warms the schedule cache for the activity rows on screen, so the first click
 * opens the task panel without a cold fetch behind the drawer. Reads the same
 * key and options the drawer host's data hook does, so the host finds the
 * schedule already there. A site usually has one schedule; a prefetch per
 * distinct number, and never again once cached (the detail never goes stale).
 */
export function usePrefetchRowSchedules(siteId: string | undefined, rows: readonly { scheduleNumber: string }[] | undefined) {
  const queryClient = useQueryClient();
  const numbers = [...new Set((rows ?? []).map((row) => row.scheduleNumber))].sort().join('|');
  useEffect(() => {
    if (!siteId || numbers === '') return;
    for (const scheduleNumber of numbers.split('|')) {
      void queryClient.prefetchQuery({
        queryKey: scheduleKey(siteId, scheduleNumber),
        queryFn: ({ signal }) => fetchSchedule(siteId, scheduleNumber, signal),
        staleTime: Infinity,
        retry: retryUnlessProviderError,
      });
    }
  }, [queryClient, siteId, numbers]);
}
